import * as React from 'react';
import {
  keyframes,
  assignProps,
  createViewConfig,
  createGenericView,
} from '@sail/engine';
import {tokens} from '@sail/tokens';
import type {View} from '../../view';
import {view} from '../../view';
import {css} from '../../css';
import useDebouncedValue from '../utils/useDebouncedValue';

export type CodePuncherDigitData = {
  value: string;
  active?: boolean;
  disabled?: boolean;
  error?: boolean;
  success?: boolean;
  loading?: boolean;
  isRangeSelection?: boolean;
};

export type CodePuncherDigitProps = View.IntrinsicElement<
  'div',
  {
    hasCaret?: boolean;
    isHighlighted?: boolean;
  } & CodePuncherDigitData
>;

export const unstable_CodePuncherDigitConfig = createViewConfig({
  props: {} as CodePuncherDigitProps,
  name: 'unstable_CodePuncherDigit',
  defaults: {
    value: '',
  },
});

const caretBlinkKeyframes = keyframes({
  '0%, 70%, 100%': {
    opacity: 1,
  },
  '20%, 50%': {
    opacity: 0,
  },
});

const FakeCaret = createGenericView(() => {
  return (
    <view.div
      css={{
        width: 'space.1',
        height: 'large',
        borderRadius: 'xsmall',
        backgroundColor: 'currentColor',
        pointerEvents: 'none',
        flexShrink: 0,
        animation: `${caretBlinkKeyframes} 1.25s ease-out infinite`,
      }}
    />
  );
});

export const unstable_CodePuncherDigit =
  unstable_CodePuncherDigitConfig.createView(
    ({
      value,
      hasCaret,
      isHighlighted,
      isRangeSelection: _isRangeSelection,
      ...props
    }) => {
      const isVisuallyHighlighted = useDebouncedValue(
        isHighlighted ?? false,
        50,
      );

      return (
        <view.div {...props}>
          <view.div
            css={{stack: 'x', alignItems: 'center', justifyContent: 'center'}}
          >
            <view.span
              css={[
                css({
                  font: 'heading.medium',
                  fontWeight: 'semibold',
                  transition: `background-color ${tokens['animation.duration.short']} ${tokens['animation.easing']}`,
                }),
                isVisuallyHighlighted
                  ? css({backgroundColor: tokens['color.primary.100']})
                  : css({backgroundColor: 'transparent'}),
              ]}
            >
              {value}
            </view.span>
            {hasCaret ? <FakeCaret /> : null}
          </view.div>
        </view.div>
      );
    },
    {
      css: {
        width: '44px',
        height: 'space.700',

        position: 'relative',

        stack: 'x',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'form',

        color: 'text',

        cursor: 'pointer',
        userSelect: 'none',

        borderRadius: 'medium',

        transition: [
          `border-color ${tokens['animation.duration.medium']} ${tokens['animation.easing']}`,
          `border-width ${tokens['animation.duration.medium']} ${tokens['animation.easing']}`,
          `background-color ${tokens['animation.duration.short']} ${tokens['animation.easing']}`,
          `color ${tokens['animation.duration.short']} ${tokens['animation.easing']}`,
          `opacity ${tokens['animation.duration.short']} ${tokens['animation.easing']}`,
        ].join(', '),
      },
      uses: [
        assignProps<CodePuncherDigitProps>((props) => ({
          hasCaret: props.active && !props.isRangeSelection && !props.loading,
          isHighlighted:
            props.active && !!props.value && !!props.isRangeSelection,
        })),
      ],
      variants: {
        disabled: {
          true: {
            css: {
              pointerEvents: 'none',
              color: 'form.disabled',
              backgroundColor: 'form.disabled',
            },
          },
          false: {},
        },
        active: {
          true: {
            css: {
              zIndex: 1,
              borderStyle: 'solid',
              borderWidth: tokens['border.width.medium'],
              borderColor: tokens['color.hue.slate.900'],
            },
          },
          false: {
            css: {
              borderStyle: 'solid',
              borderWidth: tokens['border.width.small'],
              borderColor: tokens['color.border'],
            },
          },
        },
        error: {
          true: {
            css: {
              borderStyle: 'solid',
              borderWidth: tokens['border.width.medium'],
              borderColor: 'feedback.critical.emphasized',
              backgroundColor: 'feedback.critical',
              textColor: 'onFeedback.critical',
            },
          },
          false: {},
        },
        success: {
          true: {
            css: {
              borderStyle: 'solid',
              borderWidth: tokens['border.width.medium'],
              borderColor: 'feedback.success',
              backgroundColor: 'feedback.success',
              textColor: 'onFeedback.success',
            },
          },
          false: {},
        },
        loading: {
          true: {
            css: {
              opacity: 0.5,
            },
          },
          false: {},
        },
      },
    },
  );
